import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Check, Leaf } from "lucide-react";
import { useLang } from "@/hooks/use-language";
import { useSmartBack } from "@/hooks/use-smart-back";
import {
  getCurrentJourney,
  completeStep,
} from "@/lib/journeys.functions";
import {
  STEP_KIND_META,
  TODAY_LABEL,
  SOON_LABEL,
  DONE_LABEL,
  WEEK_DONE,
  type JourneyStepKind,
} from "@/lib/journey-copy";

export const Route = createFileRoute("/_authenticated/app/child/$childId/journey")({
  component: JourneyScreen,
});

type Step = {
  id: string;
  day_index: number;
  step_kind: JourneyStepKind;
  content_id: string | null;
  prompt_en: string | null;
  prompt_ar: string | null;
  requires_parent_confirm: boolean;
  est_minutes: number;
};

type Week = {
  id: string;
  title_en: string;
  title_ar: string;
  intro_en: string | null;
  intro_ar: string | null;
  week_number: number;
  value_code: string;
};

function JourneyScreen() {
  const { childId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useSmartBack(() => navigate({ to: "/app/child/$childId", params: { childId } }));
  const { lang } = useLang();
  const isAr = lang === "ar";

  const fetchJourney = useServerFn(getCurrentJourney);
  const completeFn = useServerFn(completeStep);

  const [week, setWeek] = useState<Week | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [doneSet, setDoneSet] = useState<Set<string>>(new Set());
  const [currentDay, setCurrentDay] = useState<number>(1);
  const [pauseUntil, setPauseUntil] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const r = await fetchJourney({ data: { childId } });
    if (r.ok) {
      setWeek(r.week as Week | null);
      setSteps((r.steps ?? []) as Step[]);
      setDoneSet(new Set((r.completions ?? []).map((c: any) => c.journey_step_id)));
      setCurrentDay(r.progress?.current_day ?? 1);
      setPauseUntil((r as any).pauseUntil ?? null);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [childId]);

  const todayStep = useMemo(
    () => steps.find((s) => s.day_index === currentDay) ?? steps[0],
    [steps, currentDay],
  );

  const handleComplete = async (step: Step) => {
    setBusy(step.id);
    await completeFn({ data: { childId, stepId: step.id, payloadKind: "tap" } });
    await load();
    setBusy(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="size-10 rounded-full border-4 border-clay border-t-primary animate-spin" />
      </div>
    );
  }

  if (!week) {
    const inPause = !!pauseUntil && new Date(pauseUntil).getTime() > Date.now();
    const days = inPause
      ? Math.max(1, Math.ceil((new Date(pauseUntil!).getTime() - Date.now()) / 86400000))
      : 0;
    return (
      <div className="px-5 py-10 max-w-2xl mx-auto text-center">
        <button onClick={goBack} className="mb-6 inline-flex items-center gap-2 text-sm font-bold text-foreground/60 hover:text-foreground">
          <ArrowLeft className="size-4" /> {isAr ? "رجوع" : "Back"}
        </button>
        <div className="text-5xl mb-4">{inPause ? "🌿" : "🌳"}</div>
        <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-2xl mb-3`}>
          {inPause
            ? isAr ? "وقفة لطيفة بين رحلتين" : "A gentle pause between journeys"
            : isAr ? WEEK_DONE.ar : WEEK_DONE.en}
        </h1>
        <p className="text-foreground/70 mb-8">
          {inPause
            ? isAr
              ? `رحلة جديدة ستستيقظ بعد ${days === 1 ? "يوم واحد" : `${days} أيام`}.`
              : `A new journey will wake up in ${days === 1 ? "1 day" : `${days} days`}.`
            : isAr ? "بينما تنتظر، خذ لحظة هادئة." : "While you wait, take a calm moment."}
        </p>
        <Link
          to="/app/child/$childId"
          params={{ childId }}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-2xl font-bold"
        >
          <Leaf className="size-4" />
          {isAr ? "العودة إلى البيت" : "Back home"}
        </Link>
      </div>
    );
  }

  const stepByDay = new Map(steps.map((s) => [s.day_index, s] as const));
  const DAYS = [1, 2, 3, 4, 5, 6, 7];

  return (
    <div className="px-5 py-6 max-w-2xl mx-auto">
      <button
        onClick={goBack}
        className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-foreground/60 hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> {isAr ? "رجوع" : "Back"}
      </button>

      {/* Hero: this week’s value */}
      <div className="rounded-3xl bg-gradient-to-br from-primary/10 via-accent/10 to-background border-2 border-clay/30 p-6 mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-foreground/50 mb-2">
          {isAr ? `الأسبوع ${week.week_number}` : `Week ${week.week_number}`}
        </p>
        <h1 className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl mb-3 tracking-tight`} dir={isAr ? "rtl" : "ltr"}>
          {isAr ? week.title_ar : week.title_en}
        </h1>
        {(isAr ? week.intro_ar : week.intro_en) && (
          <p className="text-foreground/70 leading-relaxed" dir={isAr ? "rtl" : "ltr"}>
            {isAr ? week.intro_ar : week.intro_en}
          </p>
        )}

        {/* Soft 7-leaf progress (rest days render as the lightest dot) */}
        <div className="mt-5 flex items-center gap-2 justify-center">
          {DAYS.map((d) => {
            const s = stepByDay.get(d);
            const done = s ? doneSet.has(s.id) : false;
            const isToday = d === currentDay;
            const isRest = !s;
            return (
              <div
                key={d}
                className={[
                  "size-7 rounded-full flex items-center justify-center transition-all",
                  done
                    ? "bg-primary text-primary-foreground"
                    : isToday
                    ? "bg-primary/20 border-2 border-primary"
                    : isRest
                    ? "bg-clay/20"
                    : "bg-clay/40",
                ].join(" ")}
                title={isRest ? `Day ${d} — rest` : `Day ${d}`}
              >
                {done ? <Check className="size-3.5" /> : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Today’s small step */}
      {todayStep && (
        <div className="rounded-3xl bg-card border-2 border-primary/40 p-5 mb-6 shadow-press">
          <p className="text-xs font-bold uppercase tracking-wider text-primary mb-2">
            {isAr ? TODAY_LABEL.ar : TODAY_LABEL.en}
          </p>
          <div className="flex items-start gap-4">
            <div className="text-4xl">{STEP_KIND_META[todayStep.step_kind].emoji}</div>
            <div className="flex-1">
              <h2 className={`${isAr ? "font-display-ar" : "font-display-en"} text-xl mb-1`}>
                {isAr ? STEP_KIND_META[todayStep.step_kind].label_ar : STEP_KIND_META[todayStep.step_kind].label_en}
              </h2>
              <p className="text-foreground/70 mb-4" dir={isAr ? "rtl" : "ltr"}>
                {isAr ? todayStep.prompt_ar : todayStep.prompt_en}
              </p>
              <div className="flex gap-2">
                {todayStep.content_id ? (
                  <Link
                    to="/app/watch/$id"
                    params={{ id: todayStep.content_id }}
                    className="bg-primary text-primary-foreground px-4 py-2.5 rounded-2xl font-bold text-sm"
                  >
                    {isAr ? "ابدأ بهدوء" : "Begin gently"}
                  </Link>
                ) : null}
                <button
                  onClick={() => handleComplete(todayStep)}
                  disabled={busy === todayStep.id || doneSet.has(todayStep.id)}
                  className="bg-secondary/15 text-secondary px-4 py-2.5 rounded-2xl font-bold text-sm disabled:opacity-50"
                >
                  {doneSet.has(todayStep.id)
                    ? isAr ? DONE_LABEL.ar : DONE_LABEL.en
                    : isAr ? "تم بهدوء" : "Mark gently done"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Week-at-a-glance (soft list, never blocking) */}
      <h3 className={`${isAr ? "font-display-ar" : "font-display-en"} text-lg font-bold mb-3 text-foreground/80`}>
        {isAr ? "أيام أسبوعك" : "Your week"}
      </h3>
      <div className="space-y-2">
        {DAYS.map((d) => {
          const s = stepByDay.get(d);
          if (!s) {
            // Rest day — built-in gentle pause inside the week.
            return (
              <div
                key={`rest-${d}`}
                className="rounded-2xl p-4 border-2 border-dashed border-clay/40 bg-clay/10 flex items-center gap-3"
              >
                <div className="size-9 rounded-full bg-background flex items-center justify-center text-lg shrink-0">🌿</div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground/50">
                    {isAr ? `اليوم ${d}` : `Day ${d}`}
                  </p>
                  <p className="text-sm font-bold text-foreground/60" dir={isAr ? "rtl" : "ltr"}>
                    {isAr ? "يوم راحة — لا خطوات اليوم" : "A rest day — nothing to do"}
                  </p>
                </div>
              </div>
            );
          }
          const done = doneSet.has(s.id);
          const isToday = s.day_index === currentDay;
          const isFuture = s.day_index > currentDay && !done;
          const meta = STEP_KIND_META[s.step_kind];
          return (
            <div
              key={s.id}
              className={[
                "rounded-2xl p-4 border-2 flex items-center gap-3",
                done
                  ? "bg-primary/5 border-primary/30"
                  : isToday
                  ? "bg-card border-primary/40"
                  : isFuture
                  ? "bg-clay/20 border-clay/30 opacity-70"
                  : "bg-card border-clay/30",
              ].join(" ")}
            >
              <div className="size-9 rounded-full bg-background flex items-center justify-center text-lg shrink-0">
                {done ? <Check className="size-4 text-primary" /> : meta.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground/50">
                  {isAr ? `اليوم ${s.day_index}` : `Day ${s.day_index}`}
                </p>
                <p className="text-sm font-bold truncate" dir={isAr ? "rtl" : "ltr"}>
                  {isFuture
                    ? isAr ? SOON_LABEL.ar : SOON_LABEL.en
                    : isAr ? (s.prompt_ar ?? meta.label_ar) : (s.prompt_en ?? meta.label_en)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
